import logger from './logger';

// Global error handler that will be set by AuthContext
let globalErrorHandler = null;

export const setApiErrorHandler = (handler) => {
  globalErrorHandler = handler;
};

// Utility function for making authenticated API calls
export const authenticatedFetch = async (url, options = {}) => {
  const token = localStorage.getItem('token');
  const authToken = token?.startsWith('Bearer ') ? token : `Bearer ${token}`;

  // Check if we have a FormData body - if so, don't set Content-Type
  const isFormData = options.body instanceof FormData;
  
  const defaultHeaders = {
    'Authorization': authToken,
    'Accept': 'application/json',
  };
  
  // Only set Content-Type for non-FormData requests
  if (!isFormData) {
    defaultHeaders['Content-Type'] = 'application/json';
  }

  const config = {
    method: 'GET',
    mode: 'cors',
    credentials: 'include',
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, config);

    // Handle authentication errors
    if (response.status === 401 || response.status === 403) {
      const error = new Error(`Authentication error: ${response.status}`);
      error.response = { status: response.status };
      
      if (globalErrorHandler) {
        globalErrorHandler(error);
        throw error;
      }
      
      // Fallback handling
      logger.warn('No global error handler available, falling back to direct handling');
      localStorage.clear();
      window.location.href = '/';
      throw error;
    }

    // Check for other HTTP errors
    if (!response.ok) {
      const errorText = await response.text();
      let errorData;
      
      try {
        errorData = JSON.parse(errorText);
      } catch (e) {
        errorData = { message: errorText };
      }
      
      // Safely stringify error data, avoiding circular references
      let errorMessage = 'Server error';
      try {
        errorMessage = `Server error: ${JSON.stringify(errorData)}`;
      } catch (stringifyError) {
        // If stringify fails, use a safe fallback
        errorMessage = errorData?.message || errorData?.error || errorText || `Server error: ${response.status}`;
      }
      
      const error = new Error(errorMessage);
      error.response = { 
        status: response.status, 
        data: errorData 
      };
      throw error;
    }

    return response;
  } catch (error) {
    // Handle network errors and other fetch-related errors
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      logger.error('Network error:', error);
      throw new Error('Network error: Please check your internet connection');
    }

    // Re-throw the error for the caller to handle
    throw error;
  }
};

// Helper function for GET requests
export const authenticatedGet = async (url) => {
  const response = await authenticatedFetch(url);
  return response.json();
};

// Helper function for POST requests
export const authenticatedPost = async (url, data) => {
  const response = await authenticatedFetch(url, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.json();
};

// Helper function for PUT requests
export const authenticatedPut = async (url, data) => {
  const response = await authenticatedFetch(url, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  return response.json();
};

// Helper function for DELETE requests
export const authenticatedDelete = async (url) => {
  const response = await authenticatedFetch(url, {
    method: 'DELETE',
  });
  return response.json();
}; 